import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSimCards,
  getSimCardsSummary,
  getSimCard,
  getSimCardByIccid,
  getSimCardsByAccount,
  assignSimCardAccount,
  linkSimCardTransmitter,
  getMatchStats,
  runAutoMatch,
  getSimTransmitter,
  getMatchCandidates,
  getSimSubscriptions,
  getTelnyxSimCards,
} from '../api';
import type { SimCardFilters, AssignAccountInput, LinkTransmitterInput } from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys Factory
// =============================================================================

export const simCardsKeys = {
  all: ['sim-cards'] as const,
  list: (filters?: SimCardFilters) => [...simCardsKeys.all, 'list', filters] as const,
  summary: () => [...simCardsKeys.all, 'summary'] as const,
  detail: (id: string) => [...simCardsKeys.all, 'detail', id] as const,
  byIccid: (iccid: string) => [...simCardsKeys.all, 'by-iccid', iccid] as const,
  byAccount: (accountId: string) => [...simCardsKeys.all, 'by-account', accountId] as const,
  matchStats: () => [...simCardsKeys.all, 'match-stats'] as const,
  transmitter: (simCardId: string) => [...simCardsKeys.all, 'transmitter', simCardId] as const,
  matchCandidates: (simCardId: string) => [...simCardsKeys.all, 'match-candidates', simCardId] as const,
  subscriptions: (simCardId: string) => [...simCardsKeys.all, 'subscriptions', simCardId] as const,
  telnyx: () => [...simCardsKeys.all, 'provider', 'telnyx'] as const,
  /** @deprecated Use subscriptions instead */
  contracts: (simCardId: string) => [...simCardsKeys.all, 'contracts', simCardId] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch SIM cards list with filtering and pagination
 */
export function useSimCards(filters: SimCardFilters = {}) {
  return useQuery({
    queryKey: simCardsKeys.list(filters),
    queryFn: () => getSimCards(filters),
  });
}

/**
 * Hook to fetch SIM cards summary statistics
 */
export function useSimCardsSummary() {
  return useQuery({
    queryKey: simCardsKeys.summary(),
    queryFn: getSimCardsSummary,
  });
}

/**
 * Hook to fetch the Telnyx inventory persisted by sims_api.
 */
export function useTelnyxSimCards() {
  return useQuery({
    queryKey: simCardsKeys.telnyx(),
    queryFn: getTelnyxSimCards,
  });
}

/**
 * Hook to fetch a single SIM card by ID
 */
export function useSimCard(id: string) {
  return useQuery({
    queryKey: simCardsKeys.detail(id),
    queryFn: () => getSimCard(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch a SIM card by ICCID
 */
export function useSimCardByIccid(iccid: string) {
  return useQuery({
    queryKey: simCardsKeys.byIccid(iccid),
    queryFn: () => getSimCardByIccid(iccid),
    enabled: !!iccid,
  });
}

/**
 * Hook to fetch SIM cards for an account
 */
export function useSimCardsByAccount(accountId: string) {
  return useQuery({
    queryKey: simCardsKeys.byAccount(accountId),
    queryFn: () => getSimCardsByAccount(accountId),
    enabled: !!accountId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to assign a SIM card to an account
 */
export function useAssignSimCardAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ simCardId, input }: { simCardId: string; input: AssignAccountInput }) =>
      assignSimCardAccount(simCardId, input),
    onSuccess: (simCard, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: simCardsKeys.list() });
      queryClient.invalidateQueries({ queryKey: simCardsKeys.summary() });
      queryClient.invalidateQueries({ queryKey: simCardsKeys.detail(variables.simCardId) });

      if (variables.input.account_id) {
        queryClient.invalidateQueries({
          queryKey: simCardsKeys.byAccount(variables.input.account_id),
        });
        toast.success(`SIM ${simCard.iccid} assigned to account successfully`);
      } else {
        toast.success(`SIM ${simCard.iccid} unassigned from account`);
      }
    },
    onError: (error) => {
      toast.error('Failed to assign SIM card', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to link a SIM card to a transmitter
 */
export function useLinkSimCardTransmitter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ simCardId, input }: { simCardId: string; input: LinkTransmitterInput }) =>
      linkSimCardTransmitter(simCardId, input),
    onSuccess: (simCard, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: simCardsKeys.list() });
      queryClient.invalidateQueries({ queryKey: simCardsKeys.detail(variables.simCardId) });
      queryClient.invalidateQueries({ queryKey: simCardsKeys.matchStats() });
      queryClient.invalidateQueries({ queryKey: simCardsKeys.transmitter(variables.simCardId) });

      if (variables.input.transmitter_id) {
        toast.success(`SIM ${simCard.iccid} linked to transmitter successfully`);
      } else {
        toast.success(`SIM ${simCard.iccid} unlinked from transmitter`);
      }
    },
    onError: (error) => {
      toast.error('Failed to link SIM card', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Matching Hooks
// =============================================================================

/**
 * Hook to fetch SIM-to-transmitter match statistics
 */
export function useMatchStats() {
  return useQuery({
    queryKey: simCardsKeys.matchStats(),
    queryFn: getMatchStats,
  });
}

/**
 * Hook to run auto-matching of SIM cards to transmitters
 */
export function useAutoMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: runAutoMatch,
    onSuccess: (result) => {
      // Invalidate all SIM card queries after matching
      queryClient.invalidateQueries({ queryKey: simCardsKeys.all });

      if (result.newly_matched > 0) {
        toast.success(`Auto-match completed`, {
          description: `${result.newly_matched} SIM${result.newly_matched !== 1 ? 's' : ''} matched to transmitters`,
        });
      } else {
        toast.info('Auto-match completed', {
          description: 'No new matches found',
        });
      }
    },
    onError: (error) => {
      toast.error('Auto-match failed', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to fetch transmitter details for a SIM card
 */
export function useSimTransmitter(simCardId: string) {
  return useQuery({
    queryKey: simCardsKeys.transmitter(simCardId),
    queryFn: () => getSimTransmitter(simCardId),
    enabled: !!simCardId,
  });
}

/**
 * Hook to fetch match candidates for an unmatched SIM card
 */
export function useMatchCandidates(simCardId: string) {
  return useQuery({
    queryKey: simCardsKeys.matchCandidates(simCardId),
    queryFn: () => getMatchCandidates(simCardId),
    enabled: !!simCardId,
  });
}

/**
 * Hook to fetch subscriptions for a SIM card's linked account
 */
export function useSimSubscriptions(simCardId: string) {
  return useQuery({
    queryKey: simCardsKeys.subscriptions(simCardId),
    queryFn: () => getSimSubscriptions(simCardId),
    enabled: !!simCardId,
  });
}

/** @deprecated Use useSimSubscriptions instead */
export function useSimContracts(simCardId: string) {
  return useSimSubscriptions(simCardId);
}
